<template>
  <b-modal
    :visible="show"
    :title="isEdit ? 'Chỉnh sửa IP Whitelist' : 'Thêm IP Whitelist'"
    header-bg-variant="light"
    no-close-on-backdrop
    centered
    size="lg"
    body-class="px-4 py-4"
    header-class="px-5 py-4"
    footer-class="px-4 py-3"
    @hidden="resetForm"
    @close="$emit('close')"
    @cancel="$emit('close')"
  >
    <b-form>
      <!-- Loại IP -->
      <b-form-group label="Loại IP:">
        <b-form-radio-group
          v-model="form.type"
          :options="typeOptions"
          button-variant="outline-primary"
          size="sm"
          buttons
          @change="onTypeChange"
        />
        <div class="mt-2 text-muted small">
          <span v-if="form.type === 'single'">
            <i class="fas fa-info-circle text-primary mr-1"></i><strong>Địa chỉ đơn:</strong> Dùng cho IP tĩnh cố định (như IP mạng văn phòng). Chỉ cho phép truy cập từ đúng chính xác IP này.
          </span>
          <span v-if="form.type === 'cidr'">
            <i class="fas fa-info-circle text-primary mr-1"></i><strong>Subnet (CIDR):</strong> Cho phép cả dải mạng (VD: <code>192.168.1.0/24</code>). Phù hợp cho mạng văn phòng/cửa hàng dùng IP động (chỉ đổi cụm số cuối).
          </span>
          <span v-if="form.type === 'range'">
            <i class="fas fa-info-circle text-primary mr-1"></i><strong>Dải IP:</strong> Cho phép truy cập từ bất kỳ IP nào nằm trong khoảng từ <strong>IP đầu</strong> đến <strong>IP cuối</strong>.
          </span>
        </div>
      </b-form-group>

      <!-- Địa chỉ IP / CIDR (type = single hoặc cidr) -->
      <b-form-group
        v-if="form.type !== 'range'"
        :label="form.type === 'cidr' ? 'CIDR:' : 'Địa chỉ IP:'"
        :state="!errors.ip ? null : false"
        :invalid-feedback="errors.ip"
      >
        <b-input-group size="sm">
          <b-form-input
            v-model="form.ip"
            :placeholder="form.type === 'cidr' ? 'VD: 192.168.1.0/24' : 'VD: 192.168.1.5'"
            :state="!errors.ip ? null : false"
            @input="errors.ip = ''"
          />
          <b-input-group-append>
            <b-button variant="outline-primary" :disabled="isLoadingMyIp" @click="getAndFillIp">
              <b-spinner small v-if="isLoadingMyIp" class="mr-1"></b-spinner>
              Lấy IP của tôi
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <small v-if="form.type === 'cidr' && cidrPreview" class="text-info d-block mt-1">
          {{ cidrPreview }}
        </small>
      </b-form-group>

      <!-- IP từ / IP đến (type = range) -->
      <b-row v-if="form.type === 'range'">
        <b-col cols="6">
          <b-form-group
            label="IP từ:"
            :state="!errors.ipFrom ? null : false"
            :invalid-feedback="errors.ipFrom"
          >
            <b-form-input
              v-model="form.ipFrom"
              size="sm"
              placeholder="VD: 192.168.1.1"
              :state="!errors.ipFrom ? null : false"
              @input="errors.ipFrom = ''"
            />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group
            label="IP đến:"
            :state="!errors.ipTo ? null : false"
            :invalid-feedback="errors.ipTo"
          >
            <b-form-input
              v-model="form.ipTo"
              size="sm"
              placeholder="VD: 192.168.1.100"
              :state="!errors.ipTo ? null : false"
              @input="errors.ipTo = ''"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Cửa hàng -->
      <b-form-group label="Cửa hàng:">
        <treeselect
          v-model="form.storeId"
          :options="storeOptions"
          :multiple="false"
          placeholder="Chọn cửa hàng (không bắt buộc)..."
          noResultsText="Không có kết quả"
          :searchable="true"
          :clearable="true"
          openDirection="bottom"
        />
      </b-form-group>

      <!-- Tên gợi nhớ -->
      <b-form-group label="Tên gợi nhớ:">
        <b-form-input
          v-model="form.label"
          size="sm"
          placeholder="VD: WiFi tầng 1, Mạng nội bộ..."
        />
      </b-form-group>

      <!-- Thời hạn -->
      <b-form-group label="Thời hạn:">
        <b-form-radio-group v-model="expireMode" class="mb-2">
          <b-form-radio value="unlimited">Vô thời hạn</b-form-radio>
          <b-form-radio value="date">Đến ngày</b-form-radio>
        </b-form-radio-group>
        <date-picker
          v-if="expireMode === 'date'"
          v-model="form.expireAt"
          class="form-control form-control-sm"
          :config="datePickerConfig"
          placeholder="Chọn ngày hết hạn"
        />
        <small v-if="errors.expireAt" class="text-danger">{{ errors.expireAt }}</small>
      </b-form-group>
    </b-form>

    <template #modal-footer="{ cancel }">
      <b-button variant="secondary" :disabled="isSubmitting" @click="cancel()">Hủy bỏ</b-button>
      <b-button variant="primary" :disabled="isSubmitting" @click="handleSubmit">
        <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
        {{ isEdit ? 'Cập nhật' : 'Thêm mới' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import datePicker from 'vue-bootstrap-datetimepicker';
import moment from 'moment';
import { IpWhitelistService } from './service/ip-access-control.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';

export default {
  name: 'UpsertWhitelistModal',
  components: { Treeselect, datePicker },
  props: {
    show: { type: Boolean, default: false },
    item: { type: Object, default: null },
    isEdit: { type: Boolean, default: false },
    storeOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      isSubmitting: false,
      myIp: '',
      isLoadingMyIp: false,
      expireMode: 'unlimited',
      form: {
        type: 'single',
        ip: '',
        ipFrom: '',
        ipTo: '',
        storeId: null,
        label: '',
        expireAt: '',
      },
      errors: { ip: '', ipFrom: '', ipTo: '', expireAt: '' },
      typeOptions: [
        { text: 'Địa chỉ đơn', value: 'single' },
        { text: 'Subnet (CIDR)', value: 'cidr' },
        { text: 'Dải IP', value: 'range' },
      ],
      datePickerConfig: {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        showClear: true,
        showClose: true,
        locale: 'vi',
        minDate: moment().startOf('day'),
      },
    };
  },
  computed: {
    cidrPreview() {
      if (this.form.type !== 'cidr' || !this.form.ip) return '';
      const match = this.form.ip.match(/\/(\d+)$/);
      if (!match) return '';
      const prefix = parseInt(match[1]);
      if (isNaN(prefix) || prefix < 0 || prefix > 32) return '';
      const count = Math.max(0, Math.pow(2, 32 - prefix) - 2);
      return `/${prefix} → khoảng ${count.toLocaleString('vi-VN')} địa chỉ`;
    },
    suggestedCidr() {
      if (!this.myIp) return '';
      const parts = this.myIp.split('.');
      if (parts.length === 4) {
        return `${parts[0]}.${parts[1]}.${parts[2]}.0/24`;
      }
      return '';
    },
  },
  watch: {
    item(val) {
      if (val) this.initForm(val);
    },
    show(val) {
      if (val && this.item) this.initForm(this.item);
      if (!val) this.resetForm();
    },
  },
  methods: {
    initForm(item) {
      this.form = {
        type: item.type || 'single',
        ip: item.ip || '',
        ipFrom: item.ipFrom || '',
        ipTo: item.ipTo || '',
        storeId: item.storeId || null,
        label: item.label || '',
        expireAt: item.expireAt ? moment(item.expireAt).format('DD/MM/YYYY') : '',
      };
      this.expireMode = item.expireAt ? 'date' : 'unlimited';
      this.errors = { ip: '', ipFrom: '', ipTo: '', expireAt: '' };
    },
    onTypeChange() {
      this.form.ip = '';
      this.form.ipFrom = '';
      this.form.ipTo = '';
      this.errors.ip = '';
      this.errors.ipFrom = '';
      this.errors.ipTo = '';
    },
    resetForm() {
      this.form = { type: 'single', ip: '', ipFrom: '', ipTo: '', storeId: null, label: '', expireAt: '' };
      this.expireMode = 'unlimited';
      this.errors = { ip: '', ipFrom: '', ipTo: '', expireAt: '' };
    },
    validateForm() {
      this.errors = { ip: '', ipFrom: '', ipTo: '', expireAt: '' };
      let valid = true;

      if (this.form.type === 'single' || this.form.type === 'cidr') {
        if (!this.form.ip) {
          this.errors.ip = 'Vui lòng nhập địa chỉ IP';
          valid = false;
        }
      }
      if (this.form.type === 'range') {
        if (!this.form.ipFrom) { this.errors.ipFrom = 'Vui lòng nhập IP bắt đầu'; valid = false; }
        if (!this.form.ipTo) { this.errors.ipTo = 'Vui lòng nhập IP kết thúc'; valid = false; }
      }
      if (this.expireMode === 'date' && !this.form.expireAt) {
        this.errors.expireAt = 'Vui lòng chọn ngày hết hạn';
        valid = false;
      }
      return valid;
    },
    buildPayload() {
      const payload = {
        type: this.form.type,
        storeId: this.form.storeId || null,
        label: this.form.label || null,
        expireAt: this.expireMode === 'date' && this.form.expireAt
          ? moment(this.form.expireAt, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : null,
      };
      if (this.form.type === 'range') {
        payload.ipFrom = this.form.ipFrom;
        payload.ipTo = this.form.ipTo;
      } else {
        payload.ip = this.form.ip;
      }
      return payload;
    },
    fetchMyIp() {
      if (this.myIp) return;
      this.isLoadingMyIp = true;
      IpWhitelistService.getMyIp()
        .then(({ data }) => {
          this.myIp = data?.data?.ip || '';
        })
        .catch(() => {})
        .finally(() => {
          this.isLoadingMyIp = false;
        });
    },
    fillMyIp() {
      if (this.form.type === 'single') {
        this.form.ip = this.myIp;
        this.errors.ip = '';
      } else if (this.form.type === 'cidr' && this.suggestedCidr) {
        this.form.ip = this.suggestedCidr;
        this.errors.ip = '';
      }
    },
    getAndFillIp() {
      if (this.myIp) {
        this.fillMyIp();
        makeToastSuccess('Đã điền IP hiện tại thành công!');
        return;
      }
      this.isLoadingMyIp = true;
      IpWhitelistService.getMyIp()
        .then(({ data }) => {
          this.myIp = data?.data?.ip || '';
          if (this.myIp) {
            this.fillMyIp();
            makeToastSuccess('Đã điền IP hiện tại thành công!');
          } else {
            makeToastFaile('Không nhận được IP từ hệ thống');
          }
        })
        .catch(err => {
          makeToastFaile(err?.response?.data?.message || 'Không thể lấy IP hiện tại');
        })
        .finally(() => {
          this.isLoadingMyIp = false;
        });
    },
    handleSubmit() {
      if (!this.validateForm()) return;
      this.isSubmitting = true;
      const payload = this.buildPayload();
      const request = this.isEdit
        ? IpWhitelistService.update(this.item.id, payload)
        : IpWhitelistService.create(payload);

      request
        .then(() => {
          makeToastSuccess(this.isEdit ? 'Cập nhật thành công!' : 'Thêm mới thành công!');
          this.$emit('saved');
        })
        .catch(err => makeToastFaile(err?.response?.data?.message || 'Lưu thất bại'))
        .finally(() => { this.isSubmitting = false; });
    },
  },
};
</script>
